import { useCallback, useEffect, useState } from "react";
import {
  fetchAuditorDisputes,
  fetchHospitalDisputes,
  type DisputeApi,
  type DisputesQuery,
} from "../api/disputeApi";
import { getAuthToken, getStoredAccountType } from "../auth";
import { allDisputesList, hospitalDisputes } from "../data";
import type { Dispute, DisputeListItem, DisputeStatus } from "../types";

function mapApiToListItem(row: DisputeApi): DisputeListItem {
  const status = (row.status || "inProgress") as DisputeStatus;
  const submitted =
    row.submitted_date ||
    row.createdAt ||
    (row.created_at ? new Date(row.created_at).toLocaleDateString("fr-FR") : "");

  return {
    id: String(row.id),
    displayId: Number(row.display_id ?? row.id),
    reference: row.reference || `LIT-${row.id}`,
    invoice: row.invoice || `#${row.invoice_id}`,
    company: row.company || "",
    hospital: row.hospital || "",
    amount: Number(row.amount ?? 0),
    status,
    createdAt: row.createdAt || submitted,
    submittedDate: submitted,
  };
}

function mapListItemToDispute(item: DisputeListItem): Dispute {
  return {
    id: item.id,
    reference: item.reference,
    invoice: item.invoice,
    company: item.company,
    status: item.status,
    createdAt: item.createdAt,
  };
}

export function useDisputes(query: DisputesQuery = {}) {
  const [disputes, setDisputes] = useState<DisputeListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    const accountType = getStoredAccountType();
    const isHospital = accountType === "hospital";

    if (!getAuthToken()) {
      setUsingApi(false);
      setError(null);
      if (isHospital) {
        const mock = hospitalDisputes.map((d) => ({
          ...d,
          displayId: Number.parseInt(d.id.replace(/\D/g, ""), 10) || 0,
          hospital: "",
          amount: 0,
          submittedDate: d.createdAt,
        }));
        setDisputes(
          query.status ? mock.filter((d) => d.status === query.status) : mock,
        );
      } else {
        const mock = allDisputesList;
        setDisputes(
          query.status ? mock.filter((d) => d.status === query.status) : mock,
        );
      }
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const api = isHospital
        ? await fetchHospitalDisputes(query)
        : await fetchAuditorDisputes(query);
      setDisputes((api.disputes ?? []).map(mapApiToListItem));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger les litiges.");
      setDisputes([]);
      setUsingApi(false);
    } finally {
      setLoading(false);
    }
  }, [query.status, query.q]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    disputes,
    hospitalDisputes: disputes.map(mapListItemToDispute),
    loading,
    error,
    usingApi,
    reload: load,
  };
}
